import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { brand } from "../../../../../../config/theme";
import {
  useVehiclesSocketStore,
  type VehicleSocket,
} from "../../../../../../store/admin/vehiclesSocketStore";
import {
  POSITION_EVENTS,
  useDeviceSocketFeed,
  type SocketFrame,
} from "../../../../../../hooks/useDeviceSocketFeed";
import {
  explainStatus,
  type StatusChainState,
  type StatusExplanation,
  type StatusFrameInput,
} from "../../../../../../utils/vehicleStatusExplainer";

interface VehicleLiveDataModalProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
  onClose: () => void;
}

interface Decision {
  id: number;
  at: number;
  status: string;
  rule: string;
}

type SectionId = "params" | "frames" | "decisions";

const MAX_DECISIONS = 12;

const STATUS_COLORS: Record<string, string> = {
  driving: brand.primary,
  ralenti: "#F59E0B",
  stopped: "#EF4444",
  offline: "#6B7280",
};

const STATUS_CHIPS: Record<string, string> = {
  driving: "bg-primary-500/15 text-primary-500",
  ralenti: "bg-amber-500/15 text-amber-500",
  stopped: "bg-red-500/15 text-red-500",
  offline: "bg-gray-500/15 text-gray-500",
};

const formatNumber = (value: number | null | undefined, digits = 1): string =>
  value == null ? "—" : Number(value).toFixed(digits);

const formatClock = (ms: number | null | undefined): string =>
  ms == null ? "—" : new Date(ms).toLocaleTimeString();

const formatDuration = (ms: number | null | undefined): string => {
  if (ms == null || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds} s`;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} s`;
};

const formatValue = (
  value: number | boolean | string | null | undefined,
  onText: string,
  offText: string,
  noDataText: string,
): string => {
  if (value == null) return noDataText;
  if (typeof value === "boolean") return value ? onText : offText;
  if (typeof value === "number") return Number.isInteger(value) ? `${value}` : value.toFixed(2);
  return value;
};

const BOOLEAN_LABELS: Record<string, { on: string; off: string }> = {
  ignition: { on: "vehicleCard.statusOn", off: "vehicleCard.statusOff" },
  engineCutoff: {
    on: "vehicleCard.engineCutoffActive",
    off: "vehicleCard.engineCutoffInactive",
  },
  hitch: { on: "vehicleCard.hitched", off: "vehicleCard.unhitched" },
  idle: { on: "liveData.idleYes", off: "liveData.idleNo" },
};

const frameToInput = (frame: SocketFrame): StatusFrameInput => {
  const payload = frame.payload as Record<string, any>;
  const point = (payload.point ?? payload) as Record<string, any>;
  return {
    params: point.params ?? null,
    speed: point.speed ?? null,
    ignition: point.ignition ?? null,
    engineStatus: point.params?.engine_status ?? null,
    eventCode: point.event_code ?? null,
    battery: point.battery ?? null,
    timestamp: point.timeStamp ?? null,
    lat: point.lat ?? null,
    lon: point.lon ?? null,
    brandModel: payload.brand_model ?? point.brand_model ?? null,
    brandName: payload.brand_name ?? point.brand_name ?? null,
  };
};

export default function VehicleLiveDataModal({
  vehicle,
  isDarkMode,
  onClose,
}: VehicleLiveDataModalProps) {
  const { t } = useTranslation("lite");
  const setFocusedVehicleId = useVehiclesSocketStore((state) => state.setFocusedVehicleId);
  const { frames, isConnected } = useDeviceSocketFeed(vehicle.id);
  const [openSection, setOpenSection] = useState<SectionId | null>(null);
  const [selectedFrameId, setSelectedFrameId] = useState<number | null>(null);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [liveFrameCount, setLiveFrameCount] = useState(0);

  const seed = useMemo<StatusChainState>(
    () => ({
      params: vehicle.socketParams ?? {},
      status: vehicle.status,
      idleSince: vehicle.idleSince ?? null,
      odometerKm: vehicle.odometer,
      battery: vehicle.battery,
      gpsSat: vehicle.gps_sat,
      engineCutoff: vehicle.engineCutoff,
      hitch: vehicle.hitch,
      lat: vehicle.latitude,
      lon: vehicle.longitude,
      timestamp: vehicle.lastReport._seconds,
      brandModel: vehicle.brandModel,
      brandName: vehicle.brandName,
    }),
    [],
  );

  const baseline = useMemo(
    () =>
      explainStatus(
        {
          params: vehicle.socketParams ?? null,
          speed: vehicle.speed,
          engineStatus: vehicle.engineStatus,
          eventCode: vehicle.eventCode,
          battery: vehicle.battery,
          timestamp: vehicle.lastReport._seconds,
          lat: vehicle.latitude,
          lon: vehicle.longitude,
          brandModel: vehicle.brandModel,
          brandName: vehicle.brandName,
        },
        seed,
      ),
    [seed],
  );

  const [explanation, setExplanation] = useState<StatusExplanation>(baseline);
  const chainRef = useRef<StatusChainState>(baseline.next);
  const processedRef = useRef(0);

  useEffect(() => {
    const pending = frames
      .filter((frame) => POSITION_EVENTS.has(frame.event) && frame.id > processedRef.current)
      .reverse();

    if (pending.length === 0) return;

    processedRef.current = pending[pending.length - 1].id;

    const produced = pending.reduce<{
      chain: StatusChainState;
      entries: Array<{ frame: SocketFrame; result: StatusExplanation }>;
    }>(
      (acc, frame) => {
        const result = explainStatus(frameToInput(frame), acc.chain);
        return { chain: result.next, entries: [...acc.entries, { frame, result }] };
      },
      { chain: chainRef.current, entries: [] },
    );

    chainRef.current = produced.chain;
    setExplanation(produced.entries[produced.entries.length - 1].result);
    setLiveFrameCount((count) => count + produced.entries.length);
    setDecisions((previous) =>
      [
        ...produced.entries
          .map(({ frame, result }) => ({
            id: frame.id,
            at: frame.receivedAt,
            status: result.status,
            rule: result.rule,
          }))
          .reverse(),
        ...previous,
      ].slice(0, MAX_DECISIONS),
    );
  }, [frames]);

  const selectedFrame = useMemo(
    () => frames.find((frame) => frame.id === selectedFrameId) ?? frames[0] ?? null,
    [frames, selectedFrameId],
  );

  const handleRecenter = () => {
    setFocusedVehicleId(null);
    requestAnimationFrame(() => setFocusedVehicleId(vehicle.id));
  };

  const noDataText = t("liveData.noData");
  const onText = t("vehicleCard.statusOn");
  const offText = t("vehicleCard.statusOff");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-500",
    surface: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    hover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    iconBg: isDarkMode ? "bg-[#1B3D1B]" : "bg-[#ECFFE6]",
  };

  const vehicleName = vehicle.vehicle?.name || vehicle.imei || vehicle.id;
  const brandLabel = [vehicle.brandName, vehicle.brandModel].filter(Boolean).join(" ");
  const statusColor = STATUS_COLORS[explanation.status] ?? STATUS_COLORS.offline;
  const statusChip = STATUS_CHIPS[explanation.status] ?? STATUS_CHIPS.offline;
  const missingMetrics = explanation.metrics.filter((metric) => !metric.present).length;
  const rejectedMetrics = explanation.metrics.filter((metric) => metric.rejected).length;
  const paramCount = Object.keys(explanation.params).length;

  const speedSourceLabel = t(`liveData.speedSourcesInline.${explanation.speedSource}`);
  const movement =
    explanation.effectiveSpeed != null
      ? t("liveData.movementFrom", {
          speed: formatNumber(explanation.effectiveSpeed),
          source: speedSourceLabel,
          threshold: explanation.movementThreshold,
        })
      : t("liveData.movementUnknown");

  const ruleText = t(`liveData.rules.${explanation.rule}`, {
    speed: formatNumber(explanation.effectiveSpeed),
    source: speedSourceLabel,
    movement,
    threshold: explanation.movementThreshold,
    param: explanation.idle.paramKey ?? explanation.idle.expected[0] ?? "—",
    raw: explanation.idle.paramRaw ?? "—",
    elapsed: formatDuration(explanation.idle.idleForMs),
    idleThreshold: formatDuration(explanation.idle.thresholdMs),
    previous: explanation.previousStatus
      ? t(`liveData.statuses.${explanation.previousStatus}`)
      : noDataText,
    interpolation: { escapeValue: false },
  });

  const blockTitle = (icon: string, label: string, hint?: string) => (
    <div className="flex items-center gap-2 mb-2">
      <div
        className={`w-7 h-7 rounded-lg ${themeClasses.iconBg} flex items-center justify-center shrink-0`}
      >
        <i className={`${icon} text-primary-500 text-sm`}></i>
      </div>
      <h3 className={`text-sm font-semibold ${themeClasses.text}`}>{label}</h3>
      {hint && (
        <span className={`ml-auto text-[11px] font-mono ${themeClasses.textSecondary}`}>{hint}</span>
      )}
    </div>
  );

  const sections: Array<{
    id: SectionId;
    icon: string;
    label: string;
    summary: string;
    content: React.ReactNode;
  }> = [
    {
      id: "params",
      icon: "ri-database-2-line",
      label: t("liveData.paramsTitle"),
      summary: `${paramCount}`,
      content:
        paramCount === 0 ? (
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("liveData.noParams")}
          </p>
        ) : (
          <div className="space-y-1">
            {Object.entries(explanation.params)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, value]) => {
                const changed = explanation.changedKeys.includes(key);
                return (
                  <div key={key} className="flex items-center justify-between gap-2 text-xs">
                    <span
                      className={`font-mono truncate ${changed ? "text-primary-500" : themeClasses.textSecondary}`}
                    >
                      {changed && <i className="ri-arrow-up-line text-[10px] mr-0.5"></i>}
                      {key}
                    </span>
                    <span
                      className={`shrink-0 font-mono font-medium ${changed ? "text-primary-500" : themeClasses.text}`}
                    >
                      {String(value)}
                    </span>
                  </div>
                );
              })}
          </div>
        ),
    },
    {
      id: "frames",
      icon: "ri-radar-line",
      label: t("liveData.framesTitle"),
      summary: `${liveFrameCount}`,
      content:
        frames.length === 0 ? (
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("liveData.waitingFrames")}
          </p>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1">
              {frames.slice(0, 12).map((frame) => (
                <button
                  key={frame.id}
                  type="button"
                  onClick={() => setSelectedFrameId(frame.id)}
                  className={`px-1.5 py-0.5 rounded font-mono text-[10px] cursor-pointer transition-colors ${
                    selectedFrame?.id === frame.id
                      ? "bg-primary-500/15 text-primary-500"
                      : `${themeClasses.surface} ${themeClasses.textSecondary}`
                  }`}
                >
                  {formatClock(frame.receivedAt)}
                </button>
              ))}
            </div>
            {selectedFrame && (
              <pre
                className={`max-h-52 overflow-auto rounded-lg p-2 text-[10px] leading-snug font-mono ${themeClasses.surface} ${themeClasses.textSecondary}`}
              >
                {JSON.stringify(selectedFrame.payload, null, 2)}
              </pre>
            )}
          </div>
        ),
    },
    {
      id: "decisions",
      icon: "ri-history-line",
      label: t("liveData.decisionsTitle"),
      summary: `${decisions.length}`,
      content:
        decisions.length === 0 ? (
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("liveData.waitingFrames")}
          </p>
        ) : (
          <div className="space-y-1">
            {decisions.map((decision) => (
              <div key={decision.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{
                      backgroundColor: STATUS_COLORS[decision.status] ?? STATUS_COLORS.offline,
                    }}
                  ></span>
                  <span className={`shrink-0 font-medium ${themeClasses.text}`}>
                    {t(`liveData.statuses.${decision.status}`)}
                  </span>
                  <span className={`truncate ${themeClasses.textSecondary}`}>
                    {t(`liveData.ruleNames.${decision.rule}`)}
                  </span>
                </span>
                <span className={`shrink-0 font-mono ${themeClasses.textSecondary}`}>
                  {formatClock(decision.at)}
                </span>
              </div>
            ))}
          </div>
        ),
    },
  ];

  return createPortal(
    <div className="fixed z-40 top-1/2 md:top-[54%] -translate-y-1/2 left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] max-w-[400px] md:left-auto md:translate-x-0 md:right-[10%] md:w-[380px] md:max-w-[380px]">
      <div
        className={`${themeClasses.bg} rounded-xl shadow-2xl border ${themeClasses.border} max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col`}
      >
        <div className={`p-4 border-b ${themeClasses.border}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center space-x-3 min-w-0">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white shrink-0"
                style={{ backgroundColor: statusColor }}
              >
                <i className="ri-pulse-line text-white text-lg"></i>
              </div>
              <div className="min-w-0">
                <h2 className={`text-base font-semibold truncate ${themeClasses.text}`}>
                  {vehicleName}
                </h2>
                <p className={`text-[11px] truncate ${themeClasses.textSecondary}`}>
                  {brandLabel || vehicle.device?.type || vehicle.id}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span
                className="relative flex h-2 w-2 mr-1"
                title={
                  isConnected
                    ? t("liveData.socketConnected")
                    : t("liveData.socketDisconnected")
                }
              >
                {isConnected && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-500 opacity-75"></span>
                )}
                <span
                  className={`relative inline-flex rounded-full h-2 w-2 ${isConnected ? "bg-primary-500" : "bg-red-500"}`}
                ></span>
              </span>
              <button
                type="button"
                onClick={handleRecenter}
                title={t("liveData.recenter")}
                className={`p-1.5 ${themeClasses.hover} rounded-lg transition-colors cursor-pointer`}
              >
                <i className={`ri-focus-3-line text-base ${themeClasses.textSecondary}`}></i>
              </button>
              <button
                type="button"
                onClick={onClose}
                className={`p-1.5 ${themeClasses.hover} rounded-lg transition-colors cursor-pointer`}
              >
                <i className={`ri-close-line text-lg ${themeClasses.textSecondary}`}></i>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className={`p-4 border-b ${themeClasses.border}`}>
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusChip}`}
              >
                {t(`liveData.statuses.${explanation.status}`)}
              </span>
              <span
                className={`text-[11px] font-mono ${themeClasses.textSecondary}`}
                title={t("liveData.lastReport")}
              >
                {formatClock(explanation.timestampMs)}
              </span>
              {explanation.idleSince != null && (
                <span
                  className={`ml-auto text-[11px] ${themeClasses.textSecondary}`}
                  title={t("liveData.idleFor")}
                >
                  {formatDuration(explanation.idle.idleForMs)}
                </span>
              )}
            </div>
            <p className={`text-xs leading-relaxed ${themeClasses.text}`}>{ruleText}</p>
            {!explanation.isMoving && explanation.effectiveSpeed == null && (
              <p className="mt-1.5 text-[11px] text-amber-500">
                <i className="ri-error-warning-line mr-1"></i>
                {t("liveData.noSpeedWarning")}
              </p>
            )}
            {liveFrameCount === 0 && (
              <p className={`mt-2 text-[11px] ${themeClasses.textSecondary}`}>
                <i className="ri-time-line mr-1"></i>
                {t("liveData.baselineNotice")}
              </p>
            )}
          </div>

          <div className={`p-4 border-b ${themeClasses.border}`}>
            {blockTitle(
              "ri-speed-up-line",
              t("liveData.speedTitle"),
              `${formatNumber(explanation.effectiveSpeed)} km/h`,
            )}
            <div className="space-y-1">
              {explanation.speedCandidates.map((candidate) => {
                const isUsed = candidate.source === explanation.speedSource;
                return (
                  <div
                    key={candidate.source}
                    className={`flex items-center justify-between gap-2 text-xs rounded px-1.5 py-1 ${isUsed ? "bg-primary-500/10" : ""}`}
                  >
                    <span
                      className={`flex items-center gap-1.5 min-w-0 ${isUsed ? themeClasses.text : themeClasses.textSecondary}`}
                    >
                      <i
                        className={`${isUsed ? "ri-checkbox-circle-fill text-primary-500" : "ri-subtract-line"} text-xs shrink-0`}
                      ></i>
                      <span className="truncate">
                        {t(`liveData.speedSources.${candidate.source}`)}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 font-mono ${isUsed ? `font-medium ${themeClasses.text}` : themeClasses.textSecondary}`}
                    >
                      {candidate.value != null
                        ? `${formatNumber(candidate.value)} km/h`
                        : t(`liveData.speedReasons.${candidate.reason}`)}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className={`mt-2 text-[11px] ${themeClasses.textSecondary}`}>
              {formatNumber(explanation.effectiveSpeed)} {explanation.isMoving ? ">" : "≤"}{" "}
              {explanation.movementThreshold} km/h →{" "}
              <span className={explanation.isMoving ? "text-primary-500 font-medium" : ""}>
                {explanation.isMoving
                  ? t("liveData.moving")
                  : t("liveData.notMoving")}
              </span>
              {" · Δt "}
              {explanation.deltaMs == null ? "—" : `${(explanation.deltaMs / 1000).toFixed(1)} s`}
            </p>
          </div>

          <div className={`p-4 border-b ${themeClasses.border}`}>
            {blockTitle(
              "ri-key-2-line",
              t("liveData.ignitionTitle"),
              formatValue(explanation.ignition.value, onText, offText, noDataText),
            )}
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className={themeClasses.textSecondary}>
                {t("liveData.resolvedBy")}
              </span>
              <span className={`font-mono truncate font-medium ${themeClasses.text}`}>
                {t(
                  `liveData.ignitionSources.${explanation.ignition.source}`,
                  explanation.ignition.source,
                )}
                {explanation.ignition.source === "param" && explanation.ignition.paramKey
                  ? ` (${explanation.ignition.paramKey}=${explanation.ignition.paramRaw})`
                  : ""}
                {explanation.ignition.source === "engineStatus"
                  ? ` (=${explanation.ignition.engineStatus})`
                  : ""}
              </span>
            </div>
            {explanation.ignition.source === "none" && (
              <p className={`mt-1 text-[11px] ${themeClasses.textSecondary}`}>
                {t("liveData.ignitionMissing")}:{" "}
                <span className="font-mono">
                  {explanation.ignition.expected.join(", ") || noDataText}
                </span>
              </p>
            )}
            <div className={`mt-2 pt-2 border-t ${themeClasses.border}`}>
              {explanation.idle.supported ? (
                <>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className={themeClasses.textSecondary}>
                      {t("liveData.idleSignal")}
                    </span>
                    <span className={`font-mono font-medium ${themeClasses.text}`}>
                      {explanation.idle.paramKey
                        ? `${explanation.idle.paramKey}=${explanation.idle.paramRaw}`
                        : t("liveData.idleMissing")}
                    </span>
                  </div>
                  {liveFrameCount > 0 &&
                    explanation.idle.paramKey &&
                    !explanation.idle.inCurrentFrame && (
                      <p className="mt-1 text-[11px] text-amber-500">
                        <i className="ri-error-warning-line mr-1"></i>
                        {t("liveData.carriedOver", { param: explanation.idle.paramKey })}
                      </p>
                    )}
                </>
              ) : (
                <p className={`text-[11px] ${themeClasses.textSecondary}`}>
                  {t("liveData.idleNotSupported", {
                    threshold: formatDuration(explanation.idle.thresholdMs),
                  })}
                </p>
              )}
            </div>
          </div>

          <div className={`p-4 border-b ${themeClasses.border}`}>
            {blockTitle(
              "ri-list-check-2",
              t("liveData.metricsTitle"),
              [
                missingMetrics > 0 ? t("liveData.missingCount", { total: missingMetrics }) : null,
                rejectedMetrics > 0
                  ? t("liveData.rejectedCount", { total: rejectedMetrics })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ") || t("liveData.allResolved"),
            )}
            <div className="space-y-1.5">
              {explanation.metrics.map((metric) => {
                const labels = BOOLEAN_LABELS[metric.metric];
                const detailLine =
                  metric.origin === "frameField"
                    ? `${metric.originKey} = ${metric.originRaw}`
                    : metric.origin === "param"
                      ? [
                          `${metric.paramKey} = ${metric.paramRaw}`,
                          t(`liveData.transforms.${metric.transform}`),
                          metric.scope === "general" ? null : metric.scope,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : metric.expected.length > 0
                        ? `${t("liveData.expected")}: ${metric.expected.slice(0, 3).join(", ")}`
                        : t("liveData.notApplicable");

                return (
                  <div
                    key={metric.metric}
                    className={`flex items-start justify-between gap-2 text-xs rounded px-1.5 py-1 ${metric.origin === "none" ? themeClasses.surface : ""}`}
                  >
                    <span className="min-w-0">
                      <span className={themeClasses.text}>
                        {t(`liveData.metrics.${metric.metric}`)}
                      </span>
                      <span className={`block truncate text-[10px] ${themeClasses.textSecondary}`}>
                        {t("liveData.originLabel")}:{" "}
                        <span
                          className={
                            metric.origin === "none"
                              ? themeClasses.textSecondary
                              : metric.origin === "previous"
                                ? "text-amber-500"
                                : "text-primary-500"
                          }
                        >
                          {t(`liveData.origins.${metric.origin}`)}
                        </span>
                      </span>
                      <span
                        className={`block truncate font-mono text-[10px] ${themeClasses.textSecondary}`}
                      >
                        {detailLine}
                      </span>
                      {metric.unusedParam && (
                        <span
                          className={`block truncate font-mono text-[10px] ${themeClasses.textSecondary}`}
                        >
                          {t("liveData.unusedParam", {
                            key: metric.paramKey,
                            raw: metric.paramRaw,
                            transform: metric.transform,
                          })}
                        </span>
                      )}
                      {metric.rejected && (
                        <span className="block text-[10px] text-amber-500">
                          {metric.validRange
                            ? t("liveData.rejectedRawWithRange", {
                                raw: metric.paramRaw,
                                transform: metric.transform,
                                range: metric.validRange,
                                interpolation: { escapeValue: false },
                              })
                            : t("liveData.rejectedRaw", {
                                raw: metric.paramRaw,
                                transform: metric.transform,
                              })}
                        </span>
                      )}
                      {metric.origin === "param" && !metric.inCurrentFrame && (
                        <span className="block text-[10px] text-amber-500">
                          {t("liveData.carriedOver", { param: metric.paramKey })}
                        </span>
                      )}
                    </span>
                    <span
                      className={`shrink-0 font-mono font-medium ${metric.origin === "none" ? themeClasses.textSecondary : themeClasses.text}`}
                    >
                      {formatValue(
                        metric.value,
                        labels ? t(labels.on) : onText,
                        labels ? t(labels.off) : offText,
                        noDataText,
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {sections.map((section) => {
            const isOpen = openSection === section.id;
            return (
              <div key={section.id} className={`border-b ${themeClasses.border} last:border-b-0`}>
                <button
                  type="button"
                  onClick={() => setOpenSection(isOpen ? null : section.id)}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 ${themeClasses.hover} transition-colors cursor-pointer`}
                >
                  <i className={`${section.icon} text-primary-500 text-sm shrink-0`}></i>
                  <span className={`text-sm truncate ${themeClasses.text}`}>{section.label}</span>
                  <span
                    className={`ml-auto shrink-0 text-[11px] font-mono ${themeClasses.textSecondary}`}
                  >
                    {section.summary}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-sm shrink-0 ${themeClasses.textSecondary} transition-transform ${isOpen ? "rotate-180" : ""}`}
                  ></i>
                </button>
                {isOpen && <div className="px-4 pb-3">{section.content}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
